import React, { useEffect, useRef, useImperativeHandle, forwardRef, useId } from 'react';
import { Platform } from 'react-native';
import { TURNSTILE_SITE_KEY } from '@/constants/config';
import { useTheme } from '@/context/ThemeContext';
import { loadTurnstile, verificationError } from '@/lib/turnstile';

const darkThemes = ["sepia", "muted", "night", "smoke", "black"];
const MAX_RESET_ATTEMPTS = 3;
const CHALLENGE_TIMEOUT_MS = 120000;
// These configuration/client errors cannot be repaired by resetting the widget.
// https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes/
const NON_RETRYABLE_ERRORS = new Set(['110100', '110110', '110200', '200100', '400020', '400070']);

const Turnstile = forwardRef(({ onError }, ref) => {
  const widgetId = useRef(null);
  const promiseHandlers = useRef(null);
  const { theme } = useTheme();
  
  // Use unique ID for each instance to support multiple forms on same page
  const containerId = `turnstile-${useId().replace(/:/g, '')}`;

  // Expose execute method that returns a Promise with the token
  useImperativeHandle(ref, () => ({
    execute: () => {
      // Keyboard submits and button clicks can arrive before React rerenders.
      if (promiseHandlers.current) return promiseHandlers.current.promise;
      const attempt = { resetAttempts: 0, timer: null };
      attempt.promise = new Promise((resolve, reject) => {
        attempt.resolve = resolve;
        attempt.reject = reject;
      });
      promiseHandlers.current = attempt;
      const isActive = () => promiseHandlers.current === attempt;
      const finish = (error, token) => {
        if (!isActive()) return;
        promiseHandlers.current = null;
        clearTimeout(attempt.timer);
        if (error) attempt.reject(error);
        else attempt.resolve(token);
      };
      attempt.cancel = () => finish(verificationError('cancelled', 'Component unmounted'));

      const run = async () => {
        try {
          if (!TURNSTILE_SITE_KEY) {
            throw verificationError('unavailable', 'Turnstile site key is not configured');
          }

          const api = await loadTurnstile();
          // The form may have closed while the shared script was loading.
          if (!isActive()) return;
          const container = document.getElementById(containerId);
          if (!container) {
            throw verificationError('unavailable', 'Turnstile container not found');
          }

          // Remove existing widget if any
          if (widgetId.current !== null) {
            try {
              api.remove(widgetId.current);
            } catch (e) {
              // Ignore removal errors
            }
            widgetId.current = null;
          }

          attempt.timer = setTimeout(() => {
            finish(verificationError('timeout', 'Browser security check timed out'));
          }, CHALLENGE_TIMEOUT_MS);

          // Render and execute widget
          widgetId.current = api.render(container, {
            sitekey: TURNSTILE_SITE_KEY,
            size: 'flexible',
            execution: 'execute',
            // This component owns retries and releases the form on failure.
            retry: 'never',
            'refresh-expired': 'never',
            'refresh-timeout': 'never',
            callback: (token) => {
              if (typeof token !== 'string' || !token.trim()) {
                finish(verificationError('failed', 'Browser security check returned no token'));
              } else {
                finish(null, token);
              }
            },
            'expired-callback': () => {
              finish(verificationError('timeout', 'Browser security check expired'));
            },
            'timeout-callback': () => {
              finish(verificationError('timeout', 'Browser security check timed out'));
            },
            'unsupported-callback': () => {
              finish(verificationError('unsupported', 'Browser does not support the security check'));
            },
            'error-callback': (error) => {
              if (!isActive()) return true;
              console.error('Turnstile error:', error);

              if (NON_RETRYABLE_ERRORS.has(String(error))) {
                finish(verificationError(String(error), 'Browser security check is unavailable'));
                return true;
              }

              if (attempt.resetAttempts >= MAX_RESET_ATTEMPTS) {
                finish(verificationError(String(error), 'Verification failed after multiple attempts'));
                return true;
              }

              attempt.resetAttempts += 1;

              // Try to reset the widget
              try {
                if (widgetId.current !== null) {
                  api.reset(widgetId.current);
                  api.execute(widgetId.current);
                }
              } catch (resetError) {
                finish(verificationError('failed', 'Browser security check failed'));
              }
              return true;
            },
            theme: darkThemes.includes(theme) ? 'dark' : 'light',
          });

          // Execute the challenge
          if (isActive()) api.execute(widgetId.current);
        } catch (error) {
          if (!isActive()) return;
          console.error('Turnstile execution error:', error);
          finish(error);
          onError?.(error.message || 'Failed to initialize verification');
        }
      };
      run();
      return attempt.promise;
    }
  }));

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      promiseHandlers.current?.cancel();
      if (typeof window !== 'undefined' && window.turnstile && widgetId.current !== null) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch (error) {
          // Ignore cleanup errors
        }
      }
      widgetId.current = null;
    };
  }, []);

  // Don't render on non-web platforms
  if (Platform.OS !== 'web') {
    return null;
  }

  return <div id={containerId} />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
